import { computed } from 'vue'
import { useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import type {
  LoginCredentials,
  RegisterData,
  ForgotPasswordData,
  ResetPasswordData,
  VerifyEmailData,
  ResendVerificationData,
  UpdateProfileData,
  DeleteAccountData,
} from '@/types/auth'

/**
 * Composable for authentication operations
 * Provides convenient access to auth store and navigation helpers
 */
export function useAuth() {
  const authStore = useAuthStore()
  const router = useRouter()

  // Computed properties
  const user = computed(() => authStore.user)
  const isAuthenticated = computed(() => authStore.isAuthenticated)
  const isEmailVerified = computed(() => authStore.isEmailVerified)
  const loading = computed(() => authStore.loading)
  const error = computed(() => authStore.error)
  const apiError = computed(() => authStore.apiError)

  // Login with redirect
  async function login(credentials: LoginCredentials, redirectTo?: string) {
    const challenge = await authStore.login(credentials)

    if (challenge) {
      return challenge
    }

    if (redirectTo) {
      await router.push(redirectTo)
    } else {
      await router.push('/')
    }

    return null
  }

  async function completeTwoFactor(challengeToken: string, code: string, redirectTo?: string) {
    await authStore.completeTwoFactor(challengeToken, code)
    await router.push(redirectTo || '/')
  }

  // Register with redirect
  async function register(data: RegisterData, redirectTo?: string) {
    try {
      await authStore.register(data)
    } catch (err) {
      // Only re-throw if registration actually failed (user not authenticated)
      // If user is authenticated but a post-auth side effect threw (e.g., analytics),
      // we should still redirect to complete the flow
      if (!authStore.isAuthenticated) {
        throw err
      }
      // Log the error but continue with redirect since user is authenticated
      console.warn('Post-registration side effect failed:', err)
    }

    if (redirectTo) {
      await router.push(redirectTo)
    } else {
      await router.push('/')
    }
  }

  // Logout with redirect
  async function logout(redirectTo = '/auth/login') {
    await authStore.logout()
    await router.push(redirectTo)
  }

  // Forgot password
  async function forgotPassword(data: ForgotPasswordData) {
    await authStore.forgotPassword(data)
  }

  // Reset password with redirect
  async function resetPassword(data: ResetPasswordData, redirectTo = '/auth/login') {
    await authStore.resetPassword(data)
    await router.push(redirectTo)
  }

  // Verify email
  async function verifyEmail(data: VerifyEmailData) {
    await authStore.verifyEmail(data)
  }

  // Resend the verification email
  async function resendVerification(data: ResendVerificationData) {
    await authStore.resendVerification(data)
  }

  // Refresh token
  async function refreshToken() {
    await authStore.refreshToken()
  }

  // Update the profile (display name)
  async function updateProfile(data: UpdateProfileData) {
    await authStore.updateProfile(data)
  }

  /**
   * Delete the account, then leave the app.
   *
   * The redirect is not cosmetic: every authenticated route guard would bounce
   * the now-anonymous session anyway, and staying put would render a shell
   * whose data no longer exists.
   */
  async function deleteAccount(data: DeleteAccountData, redirectTo = '/auth/login') {
    await authStore.deleteAccount(data)
    await router.push(redirectTo)
  }

  // Clear error
  function clearError() {
    authStore.clearError()
  }

  /**
   * Check whether the authenticated user holds a role.
   *
   * Matching is exact against the ROLE_-prefixed strings the API emits
   * (see User::getRoles on the backend), so callers pass 'ROLE_ADMIN',
   * never 'admin'. Roles refresh with the user, so a role granted
   * server-side lands on the next login or /auth/me.
   */
  function hasRole(role: string): boolean {
    return user.value?.roles?.includes(role) ?? false
  }

  // Require authentication (redirect to login if not authenticated)
  function requireAuth() {
    if (!isAuthenticated.value) {
      const currentPath = router.currentRoute.value.fullPath
      router.push({
        path: '/auth/login',
        query: { redirect: currentPath },
      })
      return false
    }
    return true
  }

  return {
    // State
    user,
    isAuthenticated,
    isEmailVerified,
    loading,
    error,
    apiError,
    // Actions
    login,
    completeTwoFactor,
    register,
    logout,
    forgotPassword,
    resetPassword,
    verifyEmail,
    resendVerification,
    refreshToken,
    updateProfile,
    deleteAccount,
    clearError,
    hasRole,
    requireAuth,
  }
}
